<template>
    <div id="navbar-container" class="pl-3 pr-5 flex flex-row justify-content-between align-items-center">
        <!-- LEFT -->
        <div v-if="!isWebView" class="h-full flex flex-column pt-2 align-items-start justify-content-center lg:flex-row lg:align-items-center lg:pt-0">
            <a class="cursor-pointer flex align-items-center justify-content-center" :href="webpageUrl" target="_blank">
                <SvgSoftronLogo :className="softronLogoClassname"/>
            </a>
            <span @click="$emit('easter-egg')" class="lg:align-self-end lg:pb-3 lg:ml-4" :style="softronSpanStyle" style="margin-left: 0rem; font-family: var(--font-family); color: var(--theme-font-color);">License Manager</span>
        </div>
        <div v-else>
            <div v-if="applicationGlobalInfo && applicationName" class="flex flex-row w-full align-items-center gap-3 pl-3">
                <img :alt="applicationName" :src="applicationGlobalInfo.icon_url" width="45" style="vertical-align: middle" />
                <span style="margin-left: 0rem; font-family: var(--font-family); color: var(--theme-font-color); font-size: var(--navbar-maintitle-font-size); font-weight: var(--font-weight-bold)">{{ applicationName }}</span>
            </div>
        </div>

        <!-- RIGHT -->
        <div class="flex flex-row gap-3 align-items-center">

            <!-- Theme toggle -->
            <div v-if="false" id="theme-toggle" class="p-1 flex align-items-center" style="border: 1px solid; border-radius: 5px;">
                <i :class="{ 'pi pi-moon': !theme.isDark, 'pi pi-sun': theme.isDark}" style="" @click="store.dispatch('theme/goToTheme', theme.isDark ? 'light' : 'dark')"/>
            </div>

            <!-- <DarkLightToggle v-if="!user || isWebView" style="width: 9.5rem;"/> -->

            <!-- Support dropdown -->
            <Menubar id="support-menubar" :model="supportMenu">
                <template #itemicon="{ item }">
                    <div style="width: 1.8rem;" class="flex flex-row align-items-center justify-content-start">
                        <SvgGuide v-if="item.uid==='guide'" className="navbar-menuitem-svg" color="var(--theme-font-color)"/>
                        <SvgHelp v-if="item.uid==='help'" className="navbar-menuitem-svg" color="var(--theme-font-color)"/>
                        <SvgEnveloppe v-if="item.uid==='contact'" className="navbar-menuitem-svg" color="var(--theme-font-color)"/>
                        <SvgGlobe v-if="item.uid==='website'" className="navbar-menuitem-svg" color="var(--theme-font-color)"/>
                    </div>
                </template>
            </Menubar>

            <!-- User block -->
            <div v-if="user" class="user-block" @click="toggleUserOp">
                <Button text style="padding: 0.1rem 0.55rem;">
                    <div class="flex flex-row justify-content-center align-items-center gap-2" style="font-weight: var(--navbar-user-font-weight); color: var(--theme-font-color);">
                        <Avatar class="avatar" size="small" shape="circle" style="height: 1.4rem; width: 1.4rem; background-color: var(--theme-font-color);">
                            <div class="flex flex-row align-items-center justify-content-center">
                                <SvgUserMe className="navbar-user-svg" color="var(--navbar-background-color)"/>
                            </div>
                        </Avatar>
                        <span style="font-size: var(--navbar-button-font-size); margin-right: 0.1rem;">{{ userBlockNameLabel }}</span>
                        <SvgChevronDown className="chevron-svg" color="var(--theme-font-color)"/>
                    </div>
                </Button>

                <!-- Overlay showing when clicking on the user block -->
                <OverlayPanel ref="userOp" id="user-op" class="user-op" style="background-color: var(--navbar-userop-background-color); color: var(--theme-font-color);" :style="`min-width: ${!isWebView ? '20' : '13'}rem; max-width: ${!isWebView ? '26' : '19'}rem;`">
                    <div class="w-full">
                        <div class="w-full flex flex-column align-items-center gap-1">
                            <Avatar class="avatar" size="xlarge" shape="circle" style="background-color: var(--avatar-background-color);">
                                <SvgUserMe className="navbar-userop-avatar-me-svg" color="var(--theme-font-color)"/>
                            </Avatar>
                            <div class="w-full flex flex-column align-items-center">
                                <span class="w-full text-center potentially-long-label" style="font-size: var(--navbar-userop-username-font-size); font-weight: var(--navbar-userop-username-font-weight);">{{ user.first_name }} {{ user.last_name }}</span>
                                <span class="w-full text-center potentially-long-label" style="font-size: var(--navbar-userop-email-font-size); font-weight: var(--navbar-userop-email-font-weight);">{{ user.email }}</span>
                            </div>
                            <div class="w-full flex justify-content-between mt-2" :class="{ 'flex-column': isWebView }" style="gap: 1rem;">
                                <router-link v-if="!isWebView" unstyled :to="{ path: '/profile'}" style="flex: 1;">
                                    <Button outlined class="profile-button w-full" @click="toggleUserOp" color="var(--button-normal-color)">
                                        <div class="w-full h-full flex flex-row align-items-center justify-content-center gap-2">
                                            <SvgEditProfile className="button-svg" color="var(--button-normal-color)"/>
                                            <span>Edit Profile</span>
                                        </div>
                                    </Button>
                                </router-link>
                                <div v-else style="flex: 1;">
                                    <Button outlined class="profile-button w-full" @click="toggleUserOp(); openWebDashboard();" color="var(--button-normal-color)">
                                        <div class="w-full h-full flex flex-row align-items-center justify-content-center gap-2">
                                            <SvgExternalLink className="button-svg" color="var(--button-normal-color)"/>
                                            <span>Open Web Dashboard</span>
                                        </div>
                                    </Button>
                                </div>
                                <div style="flex: 1;">
                                    <Button class="profile-button w-full" label="Sign out" @click="toggleUserOp(); clickedLogout()" style="background-image: linear-gradient(var(--button-danger-color-g),var(--button-danger-color)); border: 1px solid var(--button-danger-color);" :disabled="httpLogoutLoading">
                                        <div class="w-full h-full flex flex-row align-items-center justify-content-center gap-2">
                                            <SvgLogout className="button-svg" color="var(--button-primary-font-color)"/>
                                            <span>{{httpLogoutLoading ? 'Wait ...' : 'Sign Out'}}</span>
                                        </div>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </OverlayPanel>
            </div>
        </div>
    </div>
</template>

<script setup>
// --- IMPORTS ---
import { ref, computed, watchEffect } from 'vue';
// PrimeVue
import Menubar from 'primevue/menubar';
import Button from 'primevue/button';
import OverlayPanel from 'primevue/overlaypanel'
import Avatar from 'primevue/avatar';
// vue
import SvgSoftronLogo from '@/components/SVGs/SvgSoftronLogo.vue';
import SvgUserMe from '@/components/SVGs/SvgUserMe.vue';
import SvgGuide from '@/components/SVGs/SvgGuide.vue';
import SvgHelp from '@/components/SVGs/SvgHelp.vue';
import SvgEnveloppe from '@/components/SVGs/SvgEnveloppe.vue';
import SvgGlobe from '@/components/SVGs/SvgGlobe.vue';
import SvgEditProfile from '@/components/SVGs/SvgEditProfile.vue';
import SvgExternalLink from '@/components/SVGs/SvgExternalLink.vue';
import SvgLogout from '@/components/SVGs/SvgLogout.vue';
import SvgChevronDown from '../SVGs/SvgChevronDown.vue';
import DarkLightToggle from '@/components/Modules/DarkLightToggle.vue';
// Others
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import UserUtils from '@/composables/UserUtils';
import toastService from '@/composables/toastService';

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchLogout } = useApiRequest()
const httpLogoutLoading = ref(false)
const httpLogoutData = ref(null)

const router = useRouter()
const store = useStore()
const { logoutUser } = UserUtils(store, router)

// --- isWebView info ---
const isWebView = computed(() => store.state.applicationWebView.isWebView)
const applicationGlobalInfo = computed(() => store.state.applicationWebView.applicationGlobalInfo)
const applicationName = computed(() => store.state.applicationWebView.applicationName)

// --- User info ---
const user = computed(() => store.state.user.user)

// --- Theme info ---
const theme = computed(() => store.state.theme.theme)

// --- Softron Logo redirect link ---
const webpageUrl = process.env.VUE_APP_WEBPAGE_URL

// --- OverlayPanel ---
const userOp = ref();
const toggleUserOp = (event) => {
    userOp.value.toggle(event);
}

// --- Support Menu ---
const supportMenu = [{ label: 'Support', items: [
    {
        label: 'Platform Guide',
        command: () => {
            let platformGuideLink = 'https://d1ri0d36xvumy2.cloudfront.net/resources/user_manuals/SoftronLicenseManager_PlaformGuide.pdf'
            // Open a blank page to the link
            window.open(platformGuideLink, '_blank');
            // (in case it is in the web view in the app, ask the app to open it also, because the line above will not work if it is in the web view)
            if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsOpenWebPage) {
                window.webkit.messageHandlers.jsOpenWebPage.postMessage(platformGuideLink);
            }
        },
        uid: 'guide'
    },
    {
        label: 'Help Center',
        command: () => {
            let helpCenterLink = 'https://softron.zendesk.com/hc/en-us/sections/14666349935516'
            // Open a blank page to the link
            window.open(helpCenterLink, '_blank');
            // (in case it is in the web view in the app, ask the app to open it also, because the line above will not work if it is in the web view)
            if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsOpenWebPage) {
                window.webkit.messageHandlers.jsOpenWebPage.postMessage(helpCenterLink);
            }
        },
        uid: 'help'
    },
    {
        label: 'Contact Us',
        command: () => {
            let contactLink = 'https://softron.tv/about/contact'
            // Open a blank page to the link
            window.open(contactLink, '_blank');
            // (in case it is in the web view in the app, ask the app to open it also, because the line above will not work if it is in the web view)
            if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsOpenWebPage) {
                window.webkit.messageHandlers.jsOpenWebPage.postMessage(contactLink);
            }
        },
        uid: 'contact'
    },
    {
        label: 'Softron Website',
        command: () => {
            let websiteLink = 'https://softron.tv'
            // Open a blank page to the link
            window.open(websiteLink, '_blank');
            // (in case it is in the web view in the app, ask the app to open it also, because the line above will not work if it is in the web view)
            if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsOpenWebPage) {
                window.webkit.messageHandlers.jsOpenWebPage.postMessage(websiteLink);
            }
        },
        uid: 'website'
    }
]}]

// --- Logout ---
const clickedLogout = async () => {
    // Send HTTP request
    httpLogoutLoading.value = true
    try {
        httpLogoutData.value = await fetchLogout()
    } catch (err) {
        toastService.add({ severity: 'warn', summary: "Logout: Warning.", detail: 'It seems that the logout has not been correctly managed by the server.', life: 5000 });
    } finally {
        httpLogoutLoading.value = false
        logoutUser()
    }
}

// --- Open Softron License Manager Dashboard ---
const openWebDashboard = () => {
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.jsOpenWebPage) {
        window.webkit.messageHandlers.jsOpenWebPage.postMessage(webpageUrl);
    }
}

// --- Width/Height watcher ---
const windowWidth = ref(0)
const windowHeight = ref(0)
function updateWidthHeight() {
    windowWidth.value = window.innerWidth;
    windowHeight.value = window.innerHeight;
}
updateWidthHeight();
window.addEventListener('resize', updateWidthHeight);

// --- userBlockNameLabel ---
const windowWidthLimit = 750
const userBlockNameLabel = ref('')
watchEffect(() => {
    if (user.value && user.value.first_name && !user.value.last_name) {
        userBlockNameLabel.value = (windowWidth.value > windowWidthLimit) ? user.value.first_name : user.value.first_name.charAt(0).toUpperCase() + "."
    }
    else if (user.value && !user.value.first_name && user.value.last_name) {
        userBlockNameLabel.value = (windowWidth.value > windowWidthLimit) ? user.value.last_name : user.value.last_name.charAt(0).toUpperCase() + "."
    }
    else if (user.value && user.value.first_name && user.value.last_name) {
        userBlockNameLabel.value = (windowWidth.value > windowWidthLimit) ? `${user.value.first_name} ${user.value.last_name}` : [user.value.first_name, user.value.last_name].map(subname => subname.charAt(0).toUpperCase() + ".").join(" ");
    }
})

// Logo for phone
let softronSpanStyle = { fontSize: 'var(--navbar-maintitle-font-size)', fontWeight: 'var(--navbar-maintitle-font-weight)' }
let softronLogoClassname = "navbar-logo-svg"
if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) { // IF ON PHONE
    // Don't only if width>height
    softronSpanStyle.fontSize = 'var(--navbar-maintitle-phone-font-size)';
    softronLogoClassname = "navbar-logo-phone-svg"
}
</script>

<style scoped>
#navbar-container {
    position: sticky;
    top: 0;
    z-index: 999;
    height: 100%;
    background-color: var( --navbar-background-color);
    box-shadow: 0px 0px 3px var(--theme-color4);
    border-radius: 0px;
    height: 100%;
}
:deep(.p-menubar) {
    background-color: transparent;
}
:deep(.p-menuitem-link .p-menuitem-text) {
    font-size: var(--navbar-button-font-size);
    font-weight: var(--button-secondary-font-weight) !important;
}
:deep(.p-submenu-list .p-menuitem-text) {
    font-size: var(--navbar-menuitem-button-font-size) !important;
}
.selected {
    /* border-bottom: 2px solid; */
    transform: scale(1.1, 1.1);
    font-weight: bold;
    padding: 0px 40px;
    color: var( --myprimary-text-color);
    opacity: 1;
}
.user-block {
    transition: all 0.3s ease-out;
}
.user-block:hover {
    cursor: pointer;
}
#theme-toggle {
    color: var(--theme-font-color)
}
#theme-toggle:hover {
    box-shadow: 0px 0px 2px var(--theme-color4);
    cursor: pointer;
}
#avatar {
    width: 30px;
    height: 30px;
    border-radius: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
}
:deep(.p-avatar) {
    background-color: var(--user-block-font-color);
    color: var(--myprimary-text-color);
}
:deep(.p-menuitem-content) {
    background: transparent !important;
}
:deep(.p-submenu-list) {
    padding: 0;
    background-color: var(--navbar-background-color);
    border: 1px solid var(--myprimary-text-color);
}
:deep(.p-submenu-list .p-menuitem:hover) {
    background-color: var(--theme-color1);
}
:deep(.p-menubar) {
    padding: 0;
    border: none;
    box-shadow: none;
}
:deep(.p-menuitem) {
    transition: all 0.3s ease;
}
:deep(.p-menubar .p-menuitem-icon) {
    color: var(--theme-font-color);
}
:deep(.p-menubar .p-icon) {
    color: var(--theme-font-color);
}
:deep(.p-menuitem-link .p-menuitem-text, .p-menubar .p-icon) {
    color: var(--theme-font-color);
    font-weight: 200;
}
:deep(.p-submenu-list) {
    min-width: 0rem;
    width: 11rem;
}
.userop-label {
    font-size: var(--navbar-userop-label-font-size);
    font-weight: var(--navbar-userop-label-font-weight);
}
.userop-value {
    font-size: var(--navbar-userop-value-font-size);
    font-weight: var(--navbar-userop-value-font-weight);
}
.small-screen-title {
    align-self: flex-end;
    padding-bottom: 1rem;
    margin-bottom: 2rem;
    margin-left: 4rem;
}
</style>

<style>
</style>